import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, FormControl, Select, MenuItem, InputLabel, Paper, CircularProgress } from '@material-ui/core';
import * as colors from '@material-ui/core/colors';
import moment from 'moment';
import * as Actions from '../../store/actions';
import * as ProviderActions from 'app/store/actions/providers/providers.actions';

const GpuUtilizationChart = ({ clusterId, className }) => {
    const dispatch = useDispatch();
    const [chartData, setChartData] = useState(null);
    const [timeRange, setTimeRange] = useState('24h');
    const [selectedNode, setSelectedNode] = useState('all');
    
    // Get GPU metrics data from Redux store (keeping original GPU metrics)
    const { data: gpuMetrics, loading, error } = useSelector(({ clusters }) => clusters.cluster.gpuMetrics);
    
    // Also get cluster metrics for enhanced node-specific data
    const { 
        history: clusterMetricsHistory 
    } = useSelector(({ clusters }) => clusters.cluster.clusterMetrics);
    
    // Get socket-based change flags for real-time updates (same pattern as topology)
    const changedDbClusterHealth = useSelector(({ providers }) => providers.providers.changedDbClusterHealth);
    const changedDbClusters = useSelector(({ providers }) => providers.providers.changedDbClusters);
    const changedDbNodes = useSelector(({ providers }) => providers.providers.changedDbNodes);

    const timeRangeOptions = [
        { value: '1h', label: '1 Hour' },
        { value: '6h', label: '6 Hours' },
        { value: '24h', label: '24 Hours' },
        { value: '7d', label: '7 Days' },
        { value: '30d', label: '30 Days' }
    ];

    // Get available nodes for selection from cluster metrics
    const getAvailableNodes = () => {
        if (!clusterMetricsHistory || clusterMetricsHistory.length === 0) return [];
        
        const nodeSet = new Set();
        clusterMetricsHistory.forEach(metric => {
            if (metric.nodeMetrics) {
                metric.nodeMetrics.forEach(node => {
                    if (node.system?.gpu?.count && node.system.gpu.count > 0) {
                        nodeSet.add(node.hostname);
                    }
                });
            }
        });
        
        return Array.from(nodeSet).sort();
    };

    const processMetricsData = (metrics) => {
        if (!metrics || !metrics.metrics || metrics.metrics.length === 0) {
            setChartData(null);
            return;
        }

        // Group data by GPU
        const gpuDataMap = new Map();
        const timeLabels = [];
        
        // Sort metrics by timestamp
        const sortedMetrics = [...metrics.metrics].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        
        sortedMetrics.forEach(metric => {
            const timeLabel = moment(metric.timestamp).format('HH:mm');
            if (!timeLabels.includes(timeLabel)) {
                timeLabels.push(timeLabel);
            }
            
            metric.gpus.forEach(gpu => {
                const gpuKey = `GPU ${gpu.gpu_index}`;
                if (!gpuDataMap.has(gpuKey)) {
                    gpuDataMap.set(gpuKey, {
                        label: `${gpuKey} (${gpu.gpu_name || 'Unknown'})`,
                        data: [],
                        borderColor: colors.blue[500],
                        backgroundColor: 'transparent',
                        fill: false,
                        pointRadius: 2,
                        pointHoverRadius: 4,
                        borderWidth: 2
                    });
                }
                
                gpuDataMap.get(gpuKey).data.push(gpu.utilization_percent || 0);
            });
        });

        // Convert map to array and assign colors
        const datasets = Array.from(gpuDataMap.values()).map((dataset, index) => {
            const colorPalette = [
                colors.blue[500],
                colors.green[500],
                colors.orange[500],
                colors.purple[500],
                colors.red[500],
                colors.teal[500],
                colors.pink[500],
                colors.indigo[500]
            ];
            
            return {
                ...dataset,
                borderColor: colorPalette[index % colorPalette.length],
                backgroundColor: colorPalette[index % colorPalette.length] + '20'
            };
        });

        setChartData({
            labels: timeLabels,
            datasets
        });
    };

    useEffect(() => {
        if (clusterId) {
            // Fetch original GPU metrics
            dispatch(Actions.getGpuMetrics(clusterId, timeRange));
            
            // Also fetch cluster metrics for enhanced node-specific GPU data
            const hours = timeRange === '1h' ? 1 : timeRange === '6h' ? 6 : 
                         timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;
            dispatch(Actions.fetchClusterMetricsHistory(clusterId, hours, selectedNode));
        }
    }, [dispatch, clusterId, timeRange, selectedNode]);

    // Socket-based real-time updates (same pattern as topology component)
    useEffect(() => {
        if (changedDbClusterHealth && clusterId) {
            dispatch(Actions.getGpuMetrics(clusterId, timeRange));
            
            // Also update cluster metrics
            const hours = timeRange === '1h' ? 1 : timeRange === '6h' ? 6 : 
                         timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;
            dispatch(Actions.fetchClusterMetricsHistory(clusterId, hours, selectedNode));
            
            // Reset the change flag after handling it
            dispatch(ProviderActions.setChangeDbClusterHealth(false));
        }
    }, [changedDbClusterHealth, clusterId, timeRange, selectedNode, dispatch]);

    useEffect(() => {
        if (changedDbClusters && clusterId) {
            dispatch(Actions.getGpuMetrics(clusterId, timeRange));
            
            // Also update cluster metrics
            const hours = timeRange === '1h' ? 1 : timeRange === '6h' ? 6 : 
                         timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;
            dispatch(Actions.fetchClusterMetricsHistory(clusterId, hours, selectedNode));
            
            // Reset the change flag after handling it
            dispatch(ProviderActions.setChangeDbClusters(false));
        }
    }, [changedDbClusters, clusterId, timeRange, selectedNode, dispatch]);

    useEffect(() => {
        if (changedDbNodes && clusterId) {
            dispatch(Actions.getGpuMetrics(clusterId, timeRange));
            
            // Also update cluster metrics
            const hours = timeRange === '1h' ? 1 : timeRange === '6h' ? 6 : 
                         timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;
            dispatch(Actions.fetchClusterMetricsHistory(clusterId, hours, selectedNode));
            // Reset the change flag after handling it
            dispatch(ProviderActions.setChangeDbNodes(false));
        }
    }, [changedDbNodes, clusterId, timeRange, dispatch]);

    useEffect(() => {
        if (gpuMetrics) {
            processMetricsData(gpuMetrics);
        }
    }, [gpuMetrics]);

    const handleTimeRangeChange = (event) => {
        setTimeRange(event.target.value);
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 0, // Disable animations to prevent flickering
        },
        responsiveAnimationDuration: 0, // Disable responsive animations
        legend: {
            position: 'top',
            labels: {
                usePointStyle: true,
                padding: 20
            }
        },
        tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
                label: function(tooltipItem, data) {
                    return `${data.datasets[tooltipItem.datasetIndex].label}: ${tooltipItem.yLabel}%`;
                }
            }
        },
        scales: {
            xAxes: [{
                display: true,
                scaleLabel: {
                    display: true,
                    labelString: 'Time'
                }
            }],
            yAxes: [{
                display: true,
                ticks: {
                    beginAtZero: true,
                    max: 100,
                    stepSize: 10, // Add consistent step size
                    callback: function(value) {
                        return value + '%';
                    }
                },
                scaleLabel: {
                    display: true,
                    labelString: 'GPU Utilization (%)'
                }
            }]
        },
        hover: {
            mode: 'index',
            intersect: false,
            animationDuration: 0 // Disable hover animations
        }
    };

    if (loading) {
        return (
            <Paper className={`widget flex flex-col items-center justify-center p-24 ${className}`} style={{ minWidth: '600px', width: '100%', minHeight: '400px' }}>
                <CircularProgress />
                <Typography className="mt-16" color="textSecondary">
                    Loading GPU metrics...
                </Typography>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper className={`widget flex flex-col items-center justify-center p-24 ${className}`} style={{ minWidth: '600px', width: '100%', minHeight: '400px' }}>
                <Typography color="error">{error}</Typography>
            </Paper>
        );
    }

    if (!chartData || chartData.datasets.length === 0) {
        return (
            <Paper className={`widget flex flex-col p-24 ${className}`} style={{ minWidth: '600px', width: '100%' }}>
                <div className="flex flex-col items-center justify-center" style={{ width: '100%', minHeight: '300px', marginBottom: '16px' }}>
                    <Typography color="textSecondary">
                        No GPU metrics data available for the selected time range
                    </Typography>
                    <Typography className="text-12 mt-8" color="textSecondary">
                        GPU metrics will appear here as the system collects data over time
                    </Typography>
                </div>
                <div className="flex items-center justify-between" style={{ width: '100%' }}>
                    <Typography className="text-16 font-semibold" color="textPrimary">
                        GPU Utilization History
                    </Typography>
                    <FormControl size="small" style={{ minWidth: 120, flexShrink: 0 }}>
                        <InputLabel>Time Range</InputLabel>
                        <Select
                            value={timeRange}
                            onChange={handleTimeRangeChange}
                            label="Time Range"
                        >
                            {timeRangeOptions.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            </Paper>
        );
    }    return (
        <Paper className={`widget flex flex-col p-24 ${className}`} style={{ minWidth: '600px', width: '100%', height: '400px' }}>
            <div style={{ height: '320px', width: '100%', minWidth: '500px', position: 'relative', marginBottom: '16px', overflow: 'hidden' }}>
                <Line 
                    data={chartData} 
                    options={chartOptions}
                    key={`gpu-chart-${timeRange}`} // Add key to force re-render when time range changes
                />
            </div>
            <div className="flex items-center justify-between" style={{ width: '100%', height: '40px' }}>
                <Typography className="text-16 font-semibold" color="textPrimary">
                    GPU Utilization History
                </Typography>
                <div className="flex items-center space-x-12">
                    <FormControl size="small" style={{ minWidth: 120, flexShrink: 0 }}>
                        <InputLabel>Node</InputLabel>
                        <Select
                            value={selectedNode}
                            onChange={(e) => setSelectedNode(e.target.value)}
                            label="Node"
                        >
                            <MenuItem value="all">All Nodes</MenuItem>
                            {getAvailableNodes().map(node => (
                                <MenuItem key={node} value={node}>{node}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" style={{ minWidth: 120, flexShrink: 0 }}>
                        <InputLabel>Time Range</InputLabel>
                        <Select
                            value={timeRange}
                            onChange={handleTimeRangeChange}
                            label="Time Range"
                        >
                            {timeRangeOptions.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            </div>
        </Paper>
    );
};

export default GpuUtilizationChart;
